import { useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import ToggleSwitch from 'Components/pos2u/Buttons/ToggleSwitch';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
} from 'reactstrap';
import SettingsSelect from 'pages/SettingsV2/SettingsSelect';
import { useSettings } from 'pages/SettingsV2/SettingsContext';
import {
  SettingsFormValues,
  WeightBarcodeType,
} from 'pages/SettingsV2/settingsTypes';
import { TOGGLE_SWITCH_SIZE } from 'pages/SettingsV2/SettingsConstants';

// ── EAN-13 encoding tables ────────────────────────────────────────────────
const L_CODES = [
  '0001101',
  '0011001',
  '0010011',
  '0111101',
  '0100011',
  '0110001',
  '0101111',
  '0111011',
  '0110111',
  '0001011',
];
const G_CODES = [
  '0100111',
  '0110011',
  '0011011',
  '0100001',
  '0011101',
  '0111001',
  '0000101',
  '0010001',
  '0001001',
  '0010111',
];
const R_CODES = [
  '1110010',
  '1100110',
  '1101100',
  '1000010',
  '1011100',
  '1001110',
  '1010000',
  '1000100',
  '1001000',
  '1110100',
];
const PARITY = [
  'LLLLLL',
  'LLGLGG',
  'LLGGLG',
  'LLGGGL',
  'LGLLGG',
  'LGGLLG',
  'LGGGLL',
  'LGLGLG',
  'LGLGGL',
  'LGGLGL',
];

const PREFIX_DIGITS = 2;
const TOTAL_DATA_DIGITS = 12; // prefix + plu + weight, before the check digit
const DIGIT_OPTIONS = [3, 4, 5, 6, 7];
const BARCODE_TYPE_OPTIONS: { value: WeightBarcodeType; label: string }[] = [
  { value: 'weight', label: 'Product Code + Weight' },
  { value: 'price', label: 'Product Code + Selling Price' },
];

const padDigits = (value: number, len: number) =>
  String(Math.max(0, Math.trunc(value)))
    .padStart(len, '0')
    .slice(-len);

const ean13CheckDigit = (data12: string) => {
  const sum = data12
    .split('')
    .reduce((acc, ch, i) => acc + Number(ch) * (i % 2 === 0 ? 1 : 3), 0);
  return (10 - (sum % 10)) % 10;
};

const ean13Bits = (code13: string) => {
  const first = Number(code13[0]);
  const parity = PARITY[first];
  let bits = '101'; // start guard
  for (let i = 0; i < 6; i += 1) {
    const digit = Number(code13[1 + i]);
    bits += parity[i] === 'L' ? L_CODES[digit] : G_CODES[digit];
  }
  bits += '01010'; // center guard
  for (let i = 0; i < 6; i += 1) {
    bits += R_CODES[Number(code13[7 + i])];
  }
  bits += '101'; // end guard
  return bits;
};

interface BarcodeBars {
  width: number;
  height: number;
  bars: { x: number; w: number }[];
}

const buildBarcode = (
  code13: string,
  moduleWidth = 3,
  height = 72
): BarcodeBars => {
  const bits = ean13Bits(code13);
  const bars: { x: number; w: number }[] = [];
  let i = 0;
  while (i < bits.length) {
    if (bits[i] === '1') {
      let j = i;
      while (j < bits.length && bits[j] === '1') j += 1;
      bars.push({ x: i * moduleWidth, w: (j - i) * moduleWidth });
      i = j;
    } else {
      i += 1;
    }
  }
  return { width: bits.length * moduleWidth, height, bars };
};

const barcodeSvgString = (bc: BarcodeBars, caption: string) => {
  const rects = bc.bars
    .map(
      (b) =>
        `<rect x="${b.x}" y="0" width="${b.w}" height="${bc.height}" fill="#0a0a0a"/>`
    )
    .join('');
  const captionY = bc.height + 18;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${bc.width}" height="${captionY + 6}" viewBox="0 0 ${bc.width} ${captionY + 6}">`,
    `<rect width="${bc.width}" height="${captionY + 6}" fill="#ffffff"/>`,
    rects,
    `<text x="${bc.width / 2}" y="${captionY}" text-anchor="middle" font-family="Noto Sans Thai, sans-serif" font-size="14" letter-spacing="4" fill="#222">${caption}</text>`,
    '</svg>',
  ].join('');
};

// ── Segment colors (semantic, shared between chips & legend) ───────────────
type SegmentKey = 'prefix' | 'plu' | 'weight' | 'check';
type BarcodeFormatTab = 'ean13' | 'code128';
const SEG_COLORS: Record<SegmentKey, { bg: string; fg: string }> = {
  prefix: { bg: '#eef2ff', fg: '#3d5cc9' },
  plu: { bg: '#eafaf1', fg: '#2f9964' },
  weight: { bg: '#fff3e0', fg: '#b3690b' },
  check: { bg: '#fdeaea', fg: '#b4232a' },
};

const WeightBarcodeSection = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<SettingsFormValues>();
  const {
    isDirty,
    savingSection,
    saveSection,
    cancelSection,
    clearFieldError,
  } = useSettings();

  const [code128Enabled, setCode128Enabled] = useState(false);
  const [activeTab, setActiveTab] = useState<BarcodeFormatTab>('ean13');
  const testPlu = '123';
  const testWeightKg = '0.360';
  const {
    enabled,
    barcodeType,
    prefix,
    prefixLength,
    pluDigits,
    weightDigits,
  } = values.weightBarcode;
  const dirty = isDirty('weightBarcode');
  const saving = savingSection === 'weightBarcode';

  const totalDataDigits = prefixLength + pluDigits + weightDigits;
  const valid = totalDataDigits === TOTAL_DATA_DIGITS;

  const maxPlu = 10 ** pluDigits - 1;
  const maxWeightGrams = 10 ** weightDigits - 1;
  const maxWeightKg = maxWeightGrams / 1000;
  const maxPriceAmount = maxWeightGrams / 100;

  const pluNum = Number.parseInt(testPlu || '0', 10) || 0;
  const weightGrams = Math.round((Number.parseFloat(testWeightKg) || 0) * 1000);
  const priceSatang = Math.round((Number.parseFloat(testWeightKg) || 0) * 100);
  const variableValue = barcodeType === 'weight' ? weightGrams : priceSatang;

  const pluOverflow = pluNum > maxPlu;
  const weightOverflow = variableValue > maxWeightGrams;

  const normalizedPrefix = padDigits(
    Number(prefix.replace(/\D/g, '')) || 0,
    prefixLength
  );
  const pluPadded = padDigits(Math.min(pluNum, maxPlu), pluDigits);
  const weightPadded = padDigits(
    Math.min(variableValue, maxWeightGrams),
    weightDigits
  );

  const variableDigitsLabel =
    barcodeType === 'weight' ? t('Weight Digits') : t('Price Digits');
  const variableSegmentLabel =
    barcodeType === 'weight' ? t('Weight (g)') : t('Price');
  const variableRowTitle =
    barcodeType === 'weight'
      ? t('Weight {{count}} digits', { count: weightDigits })
      : t('Price {{count}} digits', { count: weightDigits });
  const variableRowDesc =
    barcodeType === 'weight'
      ? t('Weight in grams, printed automatically by the scale')
      : t('Price encoded into the barcode');
  const variableRowValue =
    barcodeType === 'weight'
      ? `${t('0.001 kg (1 g)')} - ${maxWeightKg.toFixed(3)} kg`
      : `${t('0.01 baht')} - ${maxPriceAmount.toFixed(2)} ${t('baht')}`;

  const data12 = `${normalizedPrefix}${pluPadded}${weightPadded}`;
  const checkDigit = valid ? ean13CheckDigit(data12) : 0;
  const fullBarcode = valid ? `${data12}${checkDigit}` : data12;

  const barcode = useMemo(
    () => (valid ? buildBarcode(`${data12}${checkDigit}`) : null),
    [valid, data12, checkDigit]
  );

  const segments: { key: SegmentKey; value: string; label: string }[] = [
    { key: 'prefix', value: normalizedPrefix, label: t('Prefix') },
    { key: 'plu', value: pluPadded, label: t('PLU') },
    { key: 'weight', value: weightPadded, label: variableSegmentLabel },
    {
      key: 'check',
      value: valid ? String(checkDigit) : '–',
      label: t('Check'),
    },
  ];

  const formatRows: {
    key: SegmentKey;
    title: string;
    desc: string;
    value: string;
  }[] = [
    {
      key: 'prefix',
      title: t('Prefix "{{prefix}}" ({{count}} digits)', {
        prefix: normalizedPrefix,
        count: PREFIX_DIGITS,
      }),
      desc: t(
        'Prefix marks a weight barcode and is used as the leading prefix'
      ),
      value: normalizedPrefix,
    },
    {
      key: 'plu',
      title: t('PLU {{count}} digits', { count: pluDigits }),
      desc: t(
        'Product code used in the system, supports up to {{count}} items',
        {
          count: maxPlu,
        }
      ),
      value: t('Max {{count}}', { count: maxPlu }),
    },
    {
      key: 'weight',
      title: variableRowTitle,
      desc: variableRowDesc,
      value: variableRowValue,
    },
    {
      key: 'check',
      title: t('Check Digit 1 digit'),
      desc: t('Validation digit for EAN-13 correctness'),
      value: t('Calculated automatically'),
    },
  ];

  const previewSegmentLabels: Record<SegmentKey, string> = {
    prefix: 'P',
    plu: 'PLU',
    weight: barcodeType === 'weight' ? 'VAL' : 'PRC',
    check: 'C',
  };

  const previewSummary =
    barcodeType === 'weight'
      ? `${t('PLU')} ${pluPadded} · ${t('Weight')} ${(
          Math.min(variableValue, maxWeightGrams) / 1000
        ).toFixed(3)} ${t('kg')}`
      : `${t('PLU')} ${pluPadded} · ${t('Price')} ${(
          Math.min(variableValue, maxWeightGrams) / 100
        ).toFixed(2)} ${t('baht')}`;

  const testWarn = !valid
    ? t('Cannot generate barcode: total data digits must equal 12')
    : pluOverflow
      ? t('PLU exceeds the maximum for {{count}} digits', { count: pluDigits })
      : weightOverflow
        ? barcodeType === 'weight'
          ? t('Weight exceeds the maximum for {{count}} digits', {
              count: weightDigits,
            })
          : t('Price exceeds the maximum for {{count}} digits', {
              count: weightDigits,
            })
        : '';

  const handleDownload = () => {
    if (!barcode) return;
    const svg = barcodeSvgString(barcode, fullBarcode);
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `barcode-${fullBarcode}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!barcode) return;
    const svg = barcodeSvgString(barcode, fullBarcode);
    const w = window.open('', '_blank', 'width=420,height=320');
    if (!w) return;
    w.document.write(
      `<html><head><title>${fullBarcode}</title></head><body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh">${svg}</body></html>`
    );
    w.document.close();
    w.focus();
    w.print();
  };

  const code128Tags = [
    { key: 'prefix' as SegmentKey, label: 'prefix' },
    { key: 'plu' as SegmentKey, label: t('PLU (extendable)') },
    { key: 'weight' as SegmentKey, label: t('Weight') },
    { key: 'check' as SegmentKey, label: t('Total Price') },
  ];

  return (
    <Card
      className="shadow-none border mb-3"
      style={{ fontFamily: "'Noto Sans Thai', sans-serif" }}
    >
      <CardHeader className="bg-transparent border-bottom">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <div className="d-flex align-items-start gap-3">
            <div
              className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                dirty
                  ? 'bg-warning-subtle text-warning'
                  : 'bg-success-subtle text-success'
              }`}
              style={{ width: 38, height: 38 }}
            >
              <i
                className={`fs-5 ${
                  dirty ? 'ri-error-warning-line' : 'ri-barcode-box-line'
                }`}
              />
            </div>
            <div>
              <h5 className="card-title mb-0 fw-semibold">
                {t('Weight Barcode Settings')}
              </h5>
              <p
                className={`mb-0 mt-1 fs-13 ${
                  dirty ? 'text-warning' : 'text-muted'
                }`}
              >
                {dirty ? t('You have unsaved changes') : t('All changes saved')}
              </p>
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
            <ToggleSwitch
              id="barcode-v3-enabled"
              checked={enabled}
              label={t('Enabled')}
              size={TOGGLE_SWITCH_SIZE}
              disabled={saving}
              onChange={(e) => {
                setFieldValue('weightBarcode.enabled', e.target.checked);
                clearFieldError('weightBarcode.enabled');
              }}
            />
            <div className="d-flex gap-2">
              <Button
                color="light"
                className="w-100 w-sm-auto"
                disabled={!dirty || saving}
                onClick={() => cancelSection('weightBarcode')}
              >
                {t('Cancel')}
              </Button>
              <Button
                color="primary"
                className="w-100 w-sm-auto d-inline-flex align-items-center justify-content-center"
                disabled={!dirty || saving}
                onClick={() => saveSection('weightBarcode')}
                aria-label={saving ? t('Saving...') : t('Save')}
              >
                {saving ? <Spinner size="sm" /> : t('Save')}
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardBody className={enabled ? '' : 'opacity-50 pe-none'}>
        {!valid ? (
          <div className="alert alert-danger py-2 px-3 fs-13" role="alert">
            {t(
              barcodeType === 'weight'
                ? 'Prefix + PLU + Weight = {{total}} digits, must equal exactly 12 (13 with the check digit for EAN-13) — please adjust PLU or Weight digits'
                : 'Prefix + PLU + Price = {{total}} digits, must equal exactly 12 (13 with the check digit for EAN-13) — please adjust PLU or Price digits',
              { total: totalDataDigits }
            )}
          </div>
        ) : null}

        <Nav
          tabs
          className="nav-tabs-custom mb-4"
          style={{
            gap: 4,
            // padding: '14px 22px 0',
            padding: '14px 0px 0',
            borderBottom: '1px solid #eee',
          }}
        >
          {[
            { key: 'ean13' as BarcodeFormatTab, label: t('EAN-13 (Standard)') },
            {
              key: 'code128' as BarcodeFormatTab,
              label: t('Code128 (Phase 2)'),
              hidden: true,
            },
          ].map((tab) => {
            const active = activeTab === tab.key;
            return (
              <NavItem
                key={tab.key}
                style={tab.hidden ? { display: 'none' } : undefined}
              >
                <NavLink
                  className={classnames({ active })}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setCode128Enabled(tab.key === 'code128');
                  }}
                  style={{
                    cursor: 'pointer',
                    color: active ? '#3fb878' : '#666',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: active
                      ? '2px solid #3fb878'
                      : '2px solid #8b8b8b',
                    borderRadius: 0,
                    padding: '10px 18px',
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "'Noto Sans Thai', sans-serif",
                    marginBottom: '-1px',
                    minWidth: 140,
                    textAlign: 'center',
                  }}
                >
                  {tab.label}
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>

        <div className="fw-semibold text-body fs-14 mb-3">
          {t('EAN-13 Format (Standard)')}
        </div>

        <Row className="g-3 mb-4">
          <Col xs={12} md={3}>
            <label
              className="form-label fs-13 text-muted"
              htmlFor="barcode-v3-type"
            >
              {t('Barcode Type')}
            </label>
            <SettingsSelect
              inputId="barcode-v3-type"
              value={barcodeType}
              onChange={(value) => {
                const nextType = value as WeightBarcodeType;
                setFieldValue('weightBarcode.barcodeType', nextType);
                setFieldValue(
                  'weightBarcode.valueDecimals',
                  nextType === 'weight' ? 3 : 2
                );
                clearFieldError('weightBarcode.weightDigits');
              }}
              options={BARCODE_TYPE_OPTIONS.map((option) => ({
                value: option.value,
                label: t(option.label),
              }))}
            />
          </Col>
          <Col xs={12} md={3}>
            <label
              className="form-label fs-13 text-muted"
              htmlFor="barcode-v3-prefix"
            >
              {t('Prefix (2 digits)')}
            </label>
            <Input
              id="barcode-v3-prefix"
              value={prefix}
              maxLength={prefixLength}
              className="fw-semibold"
              onChange={(e) =>
                setFieldValue(
                  'weightBarcode.prefix',
                  e.target.value.replace(/\D/g, '').slice(0, prefixLength)
                )
              }
            />
          </Col>
          <Col xs={12} md={3}>
            <label
              className="form-label fs-13 text-muted"
              htmlFor="barcode-v3-plu"
            >
              {t('Product Code Digits [PLU]')}
            </label>
            <SettingsSelect
              inputId="barcode-v3-plu"
              value={pluDigits}
              onChange={(value) => {
                setFieldValue('weightBarcode.pluDigits', Number(value));
                clearFieldError('weightBarcode.weightDigits');
              }}
              options={DIGIT_OPTIONS.map((n) => ({
                value: n,
                label: t('{{count}} digits', { count: n }),
              }))}
            />
          </Col>
          <Col xs={12} md={3}>
            <label
              className="form-label fs-13 text-muted"
              htmlFor="barcode-v3-weight"
            >
              {variableDigitsLabel}
            </label>
            <SettingsSelect
              inputId="barcode-v3-weight"
              value={weightDigits}
              onChange={(value) => {
                setFieldValue('weightBarcode.weightDigits', Number(value));
                clearFieldError('weightBarcode.weightDigits');
              }}
              options={DIGIT_OPTIONS.map((n) => ({
                value: n,
                label: t('{{count}} digits', { count: n }),
              }))}
            />
          </Col>
        </Row>

        {/* ── Test & preview ──────────────────────────────────────────── */}
        <div className="fw-semibold text-body fs-14 mb-3">
          {t('Test and Preview Barcode')}
        </div>
        <div
          className="border rounded p-3 mb-4"
          style={{ background: '#fafaf8' }}
        >
          {barcode ? (
            <div
              className="bg-white border rounded text-center px-3 py-3 px-md-4 py-md-4"
              style={{
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                className="d-flex justify-content-center"
                style={{ width: '100%' }}
              >
                <svg
                  width={barcode.width}
                  height={barcode.height}
                  viewBox={`0 0 ${barcode.width} ${barcode.height}`}
                  preserveAspectRatio="xMidYMid meet"
                  style={{
                    display: 'block',
                    width: '100%',
                    maxWidth: 470,
                    height: 96,
                  }}
                >
                  <rect
                    width={barcode.width}
                    height={barcode.height}
                    fill="#ffffff"
                  />
                  {barcode.bars.map((b, idx) => (
                    <rect
                      key={idx}
                      x={b.x}
                      y={0}
                      width={b.w}
                      height={barcode.height}
                      fill="#0a0a0a"
                    />
                  ))}
                </svg>
              </div>
              <div
                className="fw-semibold text-body mt-3"
                style={{
                  letterSpacing: 7,
                  fontSize: '1.15rem',
                  lineHeight: 1.1,
                }}
              >
                {fullBarcode}
              </div>

              <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
                {segments.map((seg) => (
                  <div
                    key={seg.key}
                    className="px-3 py-2 rounded-pill fw-semibold"
                    style={{
                      background: SEG_COLORS[seg.key].bg,
                      color: SEG_COLORS[seg.key].fg,
                      fontSize: '0.72rem',
                      letterSpacing: 1,
                      minWidth:
                        seg.key === 'plu' || seg.key === 'weight' ? 96 : 64,
                    }}
                  >
                    {previewSegmentLabels[seg.key]} {seg.value}
                  </div>
                ))}
              </div>

              <div
                className="mt-3 text-body-emphasis"
                style={{ fontSize: '0.78rem' }}
              >
                {previewSummary}
              </div>

              <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
                <Button
                  color="light"
                  onClick={handlePrint}
                  className="px-4 py-2 fw-semibold border"
                  style={{ minWidth: 132, fontSize: '0.875rem' }}
                >
                  <i className="ri-printer-line align-middle me-1" />
                  {t('Print Barcode')}
                </Button>
                <Button
                  color="light"
                  onClick={handleDownload}
                  className="px-4 py-2 fw-semibold border"
                  style={{ minWidth: 132, fontSize: '0.875rem' }}
                >
                  <i className="ri-download-2-line align-middle me-1" />
                  {t('Download (SVG)')}
                </Button>
              </div>
            </div>
          ) : null}

          {testWarn ? (
            <div className="text-danger fs-13 mt-2">{testWarn}</div>
          ) : null}
        </div>

        {/* ── Format summary ─────────────────────────────────────────── */}
        <div className="border rounded overflow-hidden mb-4">
          {formatRows.map((row, idx) => (
            <div
              key={row.key}
              className={`d-flex flex-column flex-lg-row justify-content-between gap-2 px-3 py-3 ${
                idx < formatRows.length - 1 ? 'border-bottom' : ''
              }`}
            >
              <div className="d-flex align-items-start gap-3">
                <span
                  className="rounded flex-shrink-0 mt-1"
                  style={{
                    width: 10,
                    height: 10,
                    background: SEG_COLORS[row.key].fg,
                  }}
                />
                <div>
                  <div className="fw-semibold text-body fs-13">{row.title}</div>
                  <div className="text-muted fs-12 mt-1">{row.desc}</div>
                </div>
              </div>
              <div className="fw-semibold text-body text-lg-end fs-13">
                {row.value}
              </div>
            </div>
          ))}
        </div>

        {code128Enabled ? (
          <div className="border rounded p-3" style={{ background: '#fafaf8' }}>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {code128Tags.map((tag) => (
                <span
                  key={tag.label}
                  className="px-2 py-1 rounded fw-semibold fs-13"
                  style={{
                    background: SEG_COLORS[tag.key].bg,
                    color: SEG_COLORS[tag.key].fg,
                  }}
                >
                  {tag.label}
                </span>
              ))}
            </div>
            <div className="fs-13 text-muted">
              {t(
                'Data order is fixed per the format above. Output from both phases looks the same (sku, name, weightKg, lineTotal), so the sales screen needs no changes.'
              )}
            </div>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
};

export default WeightBarcodeSection;
